/**
 * Workout Muscle Utilities
 * 
 * Provides utilities for aggregating muscle groups from workout exercises
 * and preparing them for the MuscleHighlighter component.
 */

import { getMusclesForExercises } from './muscleMapping';

/**
 * Aggregates all muscles targeted by exercises in a workout day
 * @param {Array} exercises - Array of exercise objects with name property
 * @returns {Object} Object containing primary and secondary muscles
 */
export const getWorkoutMuscles = (exercises) => {
  if (!exercises || exercises.length === 0) {
    return { primary: [], secondary: [] };
  }

  // Extract exercise names
  const exerciseNames = exercises.map(exercise => exercise.name);
  
  // Get muscles for all exercises
  const muscleData = getMusclesForExercises(exerciseNames);
  
  return muscleData;
};

/**
 * Creates a formatted muscle list for display
 * @param {Array} exercises - Array of exercise objects
 * @returns {Array} Array of unique muscle names from exercise.muscles property
 */
export const getWorkoutMuscleNames = (exercises) => {
  if (!exercises || exercises.length === 0) {
    return [];
  }

  const allMuscles = new Set();
  
  exercises.forEach(exercise => {
    if (exercise.muscles && Array.isArray(exercise.muscles)) {
      exercise.muscles.forEach(muscle => allMuscles.add(muscle));
    }
  });
  
  return Array.from(allMuscles).sort();
};

/**
 * Combines muscle data from mapping with display names
 * @param {Array} exercises - Array of exercise objects
 * @returns {Object} Combined muscle data with SVG IDs and display names
 */
export const getCombinedWorkoutMuscles = (exercises) => {
  const svgMuscles = getWorkoutMuscles(exercises);
  const displayNames = getWorkoutMuscleNames(exercises);
  
  return {
    svgMuscles,
    displayNames,
    totalExercises: exercises.length
  };
};

export default {
  getWorkoutMuscles,
  getWorkoutMuscleNames,
  getCombinedWorkoutMuscles
};